// Verificação de NIF português via API core.quickbox.pt + classificação por prefixo.
// Devolve { tipo, dados } onde:
//   tipo: 'ENI' | 'empresa' | 'particular' | 'invalido' | 'desconhecido'
//   dados: { nome, morada, codPostal, cidade } (apenas se API encontrou)
//
// Regras:
//   - Prefixo 5, 6, 8 → 'empresa' (pessoa coletiva)
//   - Prefixo 1, 2    → API valida + analisa nome:
//                       * Status true → nome → 'empresa' ou 'ENI'
//                       * Status false + checksum ok → 'ENI' (NIF recente/nao indexado AT)
//                       * Status false + checksum ko → 'invalido'
//                       * API falhou (rede) → 'ENI' como fallback
//   - Prefixo 7, 9    → 'desconhecido' (heranças, condomínios)
const API_URL = 'https://core.quickbox.pt/helper/vat/info'

// Heurística: o nome devolvido pela AT é de empresa?
function nomeIndicaEmpresa(nome) {
  if (!nome) return false
  const n = nome.toLowerCase()
  return /\b(lda|s\.?a\.?|sociedade|soc\b|unip|empresa|sgps|sarl|companhia|comercio|industria|servicos)\b/i.test(n)
    || /\b(grupo|holdings?|partners)\b/i.test(n)
}

// Valida checksum matematica do NIF portugues (algoritmo oficial).
// So verifica que os 9 digitos batem com o digito de controlo; nao confirma
// que o NIF esta activo na AT. Serve como fallback quando a API rejeita.
export function validarChecksumNIF(nif) {
  const s = String(nif ?? '').trim().replace(/\s/g, '')
  if (!/^\d{9}$/.test(s)) return false
  const digs = s.split('').map(Number)
  let soma = 0
  for (let i = 0; i < 8; i++) soma += digs[i] * (9 - i)
  const resto = soma % 11
  const controlo = resto < 2 ? 0 : 11 - resto
  return controlo === digs[8]
}

export async function verificarNIF(nif) {
  const clean = String(nif ?? '').trim().replace(/\s/g, '')
  if (clean.length !== 9 || !/^\d{9}$/.test(clean)) {
    return { tipo: 'invalido', dados: null }
  }
  const prefixo = clean[0]

  // Pessoa coletiva pelo prefixo — não precisa de API
  if (['5', '6', '8'].includes(prefixo)) {
    // Mas tentamos API para obter nome/morada
    try {
      const r = await fetch(`${API_URL}?vat=${clean}`)
      if (r.ok) {
        const d = await r.json()
        if (d?.status === true && d?.data) {
          return {
            tipo: 'empresa',
            dados: {
              nome: d.data.name || '',
              morada: d.data.address || '',
              codPostal: d.data.zip_code || '',
              cidade: d.data.city || '',
            },
          }
        }
      }
    } catch { /* ignora — devolve só tipo */ }
    return { tipo: 'empresa', dados: null }
  }

  // Pessoa singular — API decide, mas checksum valido salva de bloqueio
  if (['1', '2'].includes(prefixo)) {
    try {
      const r = await fetch(`${API_URL}?vat=${clean}`)
      if (r.ok) {
        const d = await r.json()
        if (d?.status === true && d?.data) {
          const nome = d.data.name || ''
          const tipo = nomeIndicaEmpresa(nome) ? 'empresa' : 'ENI'
          return {
            tipo,
            dados: {
              nome,
              morada: d.data.address || '',
              codPostal: d.data.zip_code || '',
              cidade: d.data.city || '',
            },
          }
        }
        // API rejeitou. Se checksum matematica valida, aceita como ENI
        // (NIFs recentes/nao indexados na AT continuam a poder registar).
        // So bloqueia se checksum tambem falha.
        if (validarChecksumNIF(clean)) return { tipo: 'ENI', dados: null }
        return { tipo: 'invalido', dados: null }
      }
    } catch { /* fallback */ }
    // API falhou (rede) → assume ENI (plataforma é B2B, admin valida depois)
    return { tipo: 'ENI', dados: null }
  }

  return { tipo: 'desconhecido', dados: null }
}

// Helper: indica se o tipo é aceitável para B2B
export function isTipoB2B(tipo) {
  return tipo === 'ENI' || tipo === 'empresa'
}

// Helper UI: label legível
export function labelTipoNIF(tipo) {
  if (tipo === 'ENI') return 'ENI'
  if (tipo === 'empresa') return 'Empresa'
  if (tipo === 'particular') return 'Particular'
  if (tipo === 'invalido') return 'NIF inválido'
  return 'Não verificado'
}
